import { limits } from "@minket/core";
import { Button } from "@minket/ui/components/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { type FormEvent, useId } from "react";
import { StepPage } from "@/components/onboarding/step-page";
import { Field, FormError } from "@/components/tenancy/field";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiKey, apiRequest } from "@/lib/query";
import { notify } from "@/lib/toast";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/project")({
  component: ProjectStep,
});

const intro =
  "A project holds one app's domains, keys, templates and audience, in a production and a preview environment.";
const asks = [
  { title: "A name and a slug", text: "Such as Lynxtrades and lynxtrades." },
  {
    title: "Streams, from its domains",
    text: "Each domain you add next sends one stream: transactional for one-to-one mail, or marketing for lists, which carry an unsubscribe link.",
  },
];

function ProjectStep() {
  const { organization, project, selectProject } = useWorkspace();
  const canManage = useCan("projects.manage");
  const orgId = organization?.organization_id ?? "";
  const queryClient = useQueryClient();
  const formId = useId();
  const create = useMutation({
    mutationFn: (body: { name: string; slug: string }) =>
      apiRequest(endpoints.createProject, { orgId }, { body }),
    onSuccess: async (created) => {
      await apiRequest(
        endpoints.updateOnboarding,
        { orgId },
        { body: { step: "project", status: "done" } },
      ).catch(() => undefined);
      void queryClient.invalidateQueries({ queryKey: apiKey(endpoints.onboarding, { orgId }) });
      await queryClient.invalidateQueries({ queryKey: apiKey(endpoints.projects, { orgId }) });
      selectProject(created.id);
      notify("success", `${created.name} created, with production and preview environments.`);
    },
  });
  if (!organization || !canManage) {
    return (
      <StepPage
        path="/onboarding/project"
        intro={intro}
        asks={asks}
        action={{
          label: "Create project",
          icon: PlusIcon,
          reason: organization
            ? "Admins and owners create projects"
            : "Create the organization first",
        }}
      />
    );
  }
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    create.mutate({
      name: String(form.get("name") ?? "").trim(),
      slug: String(form.get("slug") ?? "").trim(),
    });
  };
  return (
    <StepPage
      path="/onboarding/project"
      intro={intro}
      asks={asks}
      action={
        <Button type="submit" form={formId} loading={create.isPending}>
          <PlusIcon aria-hidden />
          Create project
        </Button>
      }
    >
      {create.data ? (
        <p role="status" className="text-sm wrap-anywhere">
          {create.data.name} is ready in {organization.name}, with production and preview
          environments.
        </p>
      ) : project ? (
        <p className="text-sm text-muted-foreground wrap-anywhere">
          {organization.name} already has {project.name}. Create another project here, or go on to
          the next step.
        </p>
      ) : null}
      <form
        id={formId}
        onSubmit={submit}
        noValidate
        aria-label="New project"
        className="grid max-w-xl gap-4 sm:grid-cols-2"
      >
        <Field label="Name" name="name" error={create.error}>
          {(control) => (
            <input {...control} required maxLength={limits.projectNameMax} autoComplete="off" />
          )}
        </Field>
        <Field label="Slug" name="slug" error={create.error}>
          {(control) => (
            <input {...control} required maxLength={limits.slugMax} autoComplete="off" />
          )}
        </Field>
        <FormError error={create.error} className="text-sm text-destructive sm:col-span-2" />
      </form>
    </StepPage>
  );
}
